/**
 * Summary:
 * PremiumSessionContent -> Template describing the content of a premium session.
 * PremiumSession -> Instance of a premium session with scheduled times, instructors, etc.
 * PremiumSessionCollateral -> Collateral of a premium session, used to display information about the session.
 * PremiumSessionEnrollment -> Enrollment of a user in a premium session.
 * PremiumSessionInterested -> Represents a user that is interested in a premium session.
 * PremiumSessionAggregate -> Aggregates all of the types above so it is easier to manipulate and access relevant session data
 */

import type { CatalogEntry } from 'types';

/**
 * Defines a placeholder for a session that is part of a premium session content.
 * While this doesnt have scheduled times, it defines the structure of the session.
 */
export type SessionDefinition = {
  modules: {
    /**
     * Examples: 'ch13', 'pr2'
     */
    content: string;
  }[];
  slug: string;
  title: string;
};

export enum PremiumSessionContentState {
  Proposed = 'proposed',
  Active = 'active',
  Retired = 'retired',
}

export enum PremiumSessionState {
  Scheduled = 'scheduled',
  Active = 'active',
  Completed = 'completed',
  Cancelled = 'cancelled',
}

export enum PremiumSessionEnrollmentState {
  Interested = 'interested',
  Enrolled = 'enrolled',
  Completed = 'completed',
  Cancelled = 'cancelled',
  Rescheduled = 'rescheduled',
  unattended = 'unattended',
}

export enum PremiumSessionInterestedState {
  Cancelled = 'cancelled',
  Active = 'active',
  Expired = 'expired',
}

export type SessionInstructor = {
  username?: string;
  name?: string;
  user_uuid?: string;
};

/**
 * A template that represents how a premium session content is structured.
 * Contains how many and what are the sessions that are part of the content. Also references the offering slug.
 * (This is not an instance with scheduled times, but a template for a session content)
 */
export type PremiumSessionContent = {
  doc_id: string;
  doc_type: 'premvt_session_content';
  /**
   * Example: 'rh124-8.2'
   */
  offering_slug: string;
  modality?: string;
  skills_assessment_url?: string;
  state?: PremiumSessionContentState;
  /**
   * Represents the sessions that are part of this content template.
   */
  sessions: SessionDefinition[];
  '@timestamp': string;
  create_time?: string;
  update_time?: string;
};

/**
 * Instance of a Premium Session with scheduled times, durations, instructors, and other details.
 * This is a session that is scheduled to happen. Red Hat defines this schedule.
 */
export type PremiumSession = {
  doc_id: string;
  doc_type: 'premvt_session';
  /**
   * Example: 'rh124-8.2'
   */
  offering_slug: string;
  /**
   * Example: 'rh403-6.6-s02' where 'rh403' is the code, '6.6' is the version, and 's02' is the session.
   */
  premvt_session_slug: string;
  seats_available?: number;
  /**
   * Number of seconds that the session will last.
   */
  duration_secs: number;
  /**
   * Language that the session will be given in.
   */
  instruction_language: string;
  /**
   * Amount of students that are enrolled in this session.
   */
  enrolled_count?: number;
  /**
   * The time when the session will start.
   */
  start_time: string;
  /**
   * The time when the session will end. (calculated from start_time + duration_secs).
   */
  end_time?: string;
  instructors?: SessionInstructor[];
  lms_offering_id?: string;
  bluejeans_id?: string;
  state: PremiumSessionState;
  capacity: number;
  update_time?: string;
  /**
   * Default timestamp field. In this case it is equivalent to the start_time.
   */
  '@timestamp'?: string;
};

/**
 * Used to associate a session content with its respective scheduled sessions.
 */
export type PremiumContentWithScheduledSessions = PremiumSessionContent & {
  scheduledSessions: PremiumSession[];
};

/**
 * Represents the collateral of a premium session.
 * Collaterals are used to display information about the session like objectives, sections, goals, etc.
 */
export type PremiumSessionCollateral = {
  doc_type: 'premvt_session_collateral';
  doc_id: string;
  /**
   * Reference to the session that this collateral is related to.
   */
  premvt_session_slug: string;
  /**
   * Example: 'rh124-8.2'
   */
  offering_slug?: string;
  create_time?: string;
  update_time?: string;
  languange?: string;
  offering_title: string;
  session_title: string;
  objectives: string[];
  sections: string[];
  goals: string[];
  '@timestamp': string;
};

type StateTransitionLog = {
  origin?: string;
  timestamp?: string;
  new_state: string;
  orig_state: string;
  state?: string;
  when?: string;
};

/**
 * Represents the enrollment of a user in a premium session (schedulable).
 * Contains information about the user, the session, and the state of the enrollment.
 * Also contains information about the survey that the user has taken.
 */
export type PremiumSessionEnrollment = {
  doc_type: 'premvt_session_enrollment';
  doc_id: string;
  username: string;
  user_uuid: string;
  /**
   * Same as update_time.
   */
  '@timestamp': string;
  /**
   * Id of the session that the user is enrolled in.
   */
  premvt_session_uuid: string;
  /**
   * Slug of the session that the user is enrolled in.
   */
  premvt_session_slug?: string;
  state: PremiumSessionEnrollmentState;
  create_time: string;
  state_transition_log: StateTransitionLog[];
  user_domain?: string;
  update_time?: string;
  subscription_id?: string;
  /**
   * diagnostics for lms_sync_state failure
   */
  lms_sync_message?: string;
  lms_sync_state?: 'clean' | 'dirty' | 'failed';
  survey_score?: number;
  /**
   * used for tracking if a user has archived the survey notification
   */
  survey_seen?: boolean;
  /**
   * Date of when survey was synced to survey application
   */
  survey_sent?: string;
  /**
   * optional text sent in the survey with the score
   */
  survey_text?: string;
};

export type PremiumSessionInterested = {
  doc_type: 'premvt_session_interested';
  doc_id: string;
  username: string;
  user_uuid: string;
  offering_slug: string;
  time_range: string;
  premvt_session_slug: string;
  state: PremiumSessionInterestedState;
  create_time: string;
  notify: boolean;
  days_after: number;
  '@timestamp': string;
};

export type EnhancedPremiumSession = PremiumSession & {
  /**
   * This will be empty or undefined if there is no enrollment
   */
  enrollments?: PremiumSessionEnrollment[];
  /**
   * This will be defined if the user is interested in the session. Be aware that this can be in different states.
   */
  interested?: PremiumSessionInterested;

  /**
   * If defined, this means that the user is enrolled in the session with a state of Enrolled or Rescheduled.
   * This is a convenience field to avoid checking the enrollments array, since that array has statuses that may invalidate the enrollment.
   */
  activeEnrollment?: PremiumSessionEnrollment;

  /**
   * Indicates if the user has attended and completed the session at least once. Based on the enrollments.
   */
  hasAttendedOnce?: boolean;
  /**
   * True if this session ends after the user subscription ends.
   */
  endsAfterSubscriptionEnd?: boolean;
};

/**
 * A compound object aggregating all the related information about a premium session.
 */
export type PremiumSessionAggregate = {
  /**
   * Main content representing the session template and structure.
   */
  content: PremiumSessionContent;
  /**
   * The catalog entry associated with the session.
   */
  catalogEntry?: CatalogEntry;
  /**
   * The scheduled sessions associated with the content.
   * They are scheduled sessions containing things like instructors, dates, and times.
   * More than one session with the same slug can exist in this list if they are scheduled at different times.
   */
  sessions?: EnhancedPremiumSession[];

  /**
   * Same as sessions, but grouped by slug.
   * This allows for easier access to all sessions with the same slug.
   * For example, if there are multiple sessions with the same slug but different times, they will be grouped together.
   * Guaranteed to be sorted by start time ascending.
   */
  sessionsGroupedBySlug?: Record<string, EnhancedPremiumSession[]>;
};

/**
 * Abstraction layer for premium session status displayed to the user
 */
export enum PremiumSessionUserFacingStatus {
  Enrolled = 'enrolled',
  Attended = 'attended',
  Schedulable = 'schedulable',
  Requested = 'requested',
  Unavailable = 'unavailable',
}

/**
 * Session slot details used by premium session cards and dashboard views.
 * This type is an abstraction that is used in the client only.
 */
export type PremiumSessionSlotInfo = {
  attendedSession?: EnhancedPremiumSession;
  currentSessionDocId?: string;
  nextSchedulableSession?: PremiumSession;
  sessionWithEnrollment?: EnhancedPremiumSession;
  modulesContentItems: Record<string, number[]>;
  userFacingStatus: PremiumSessionUserFacingStatus;
  slotSessions: PremiumSession[];
  anySessionEnrollment?: PremiumSessionEnrollment;
  hasAttendedOnce: boolean;
  isEnrolledInAnySession: boolean;
  isInterested: boolean;
};
